import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, SmartDataTable } from '@/backoffice/components/ui';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import { fetchAdminReports, type AdminReportsData } from '@/domains/admin/services/admin.service';
import { formatCurrency } from '@/backoffice/utils';
import { BarChart3, Loader2, Download } from 'lucide-react';

type Period = 'today' | 'week' | 'month';

export function AdminReportsPage() {
  const userType = useAdminContext();
  const [period, setPeriod] = useState<Period>('month');
  const [reports, setReports] = useState<AdminReportsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchAdminReports(period)
      .then(setReports)
      .catch(() => setReports(null))
      .finally(() => setLoading(false));
  }, [period]);

  const exportCsv = () => {
    if (!reports) return;
    const rows = [
      ['Période', period],
      ['Commandes', reports.orders_count],
      ['Complétées', reports.completed_orders],
      ['Revenus', reports.revenue],
      ['Satisfaction', reports.average_satisfaction],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-admin-${period}.csv`;
    a.click();
  };

  const exportPdf = () => {
    if (!reports) return;
    const html = `
      <html><head><title>Rapport Onapila</title>
      <style>body{font-family:sans-serif;padding:24px}h1{color:#4338ca}table{border-collapse:collapse;width:100%;margin-top:16px}
      th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f3f4f6}</style></head>
      <body><h1>Rapport ${period}</h1>
      <p>Commandes: ${reports.orders_count} | Complétées: ${reports.completed_orders} | Revenus: ${reports.revenue} FCFA</p>
      <p>Satisfaction: ${reports.average_satisfaction}/5 | Temps moyen: ${reports.average_delivery_hours}h</p>
      <h2>Par statut</h2><table><tr><th>Statut</th><th>Nombre</th></tr>
      ${reports.orders_by_status.map((r) => `<tr><td>${r.status}</td><td>${r.count}</td></tr>`).join('')}
      </table></body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  };

  return (
    <DashboardLayout userType={userType} title={userType === 'super_admin' ? 'Rapports globaux' : 'Rapports'}>
      <div className="flex justify-between mb-6">
        <div className="flex gap-2">
          {(['today', 'week', 'month'] as Period[]).map((p) => (
            <Button key={p} variant={period === p ? 'primary' : 'outline'} onClick={() => setPeriod(p)}>
              {p === 'today' ? "Aujourd'hui" : p === 'week' ? 'Semaine' : 'Mois'}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" leftIcon={<Download className="w-4 h-4" />} onClick={exportCsv}>
            Exporter CSV
          </Button>
          <Button variant="outline" leftIcon={<Download className="w-4 h-4" />} onClick={exportPdf}>
            Exporter PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : reports && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card><p className="text-sm text-gray-500">Commandes</p><p className="text-2xl font-bold">{reports.orders_count}</p></Card>
            <Card><p className="text-sm text-gray-500">Complétées</p><p className="text-2xl font-bold">{reports.completed_orders}</p></Card>
            <Card><p className="text-sm text-gray-500">Revenus</p><p className="text-2xl font-bold text-emerald-600">{formatCurrency(reports.revenue)}</p></Card>
            <Card><p className="text-sm text-gray-500">Temps moyen (h)</p><p className="text-2xl font-bold">{reports.average_delivery_hours}</p></Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Par statut</h3>
              <SmartDataTable
                data={reports.orders_by_status}
                columns={[
                  { key: 'status', title: 'Statut', render: (item) => item.status },
                  { key: 'count', title: 'Nombre', render: (item) => item.count },
                ]}
                keyExtractor={(item) => item.status}
                emptyMessage="Aucune donnée"
              />
            </Card>
            <Card>
              <h3 className="font-semibold mb-4">Revenus par zone</h3>
              <SmartDataTable
                data={reports.revenue_by_zone}
                columns={[
                  { key: 'zone', title: 'Zone', render: (item) => item.zone },
                  { key: 'orders', title: 'Commandes', render: (item) => item.orders },
                  { key: 'revenue', title: 'Revenus', render: (item) => formatCurrency(item.revenue) },
                ]}
                keyExtractor={(item) => item.zone}
                emptyMessage="Aucune donnée"
              />
            </Card>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
